import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowClockwise, Brain, Info, CheckCircle } from "@phosphor-icons/react";

/**
 * Job-coach view of a coachee's IQ test.
 * Shows progress (X of N answered) or final result (mean IQ + 95% CI).
 * Read-only + reset button.
 */
export default function IQTestCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/iq-test`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/iq-test/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResetting(false); }
  };

  const state = session?.state || "not_started";
  const answered = Object.keys(session?.answers || {}).length;
  const total = session?.total_items || 0;
  const result = session?.result;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="iq-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Brain size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Klassieke IQ-test</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} kan de test één keer maken binnen een tijdslimiet.
              Klik op <em>Resetten</em> om opnieuw te laten beginnen.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="iq-coach-counter">
                Status:{" "}
                <strong className="text-zinc-900">{stateLabel(state)}</strong>
                {state === "in_progress" && total > 0 && (
                  <span> — {answered} / {total} beantwoord</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting || state === "not_started"}
                data-testid="iq-coach-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Test resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="iq-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : state === "not_started" ? (
        <Empty coacheeName={coacheeName} testid="iq-coach-empty" text="heeft de IQ-test nog niet gestart" />
      ) : state === "in_progress" ? (
        <div className="neuro-card p-6 bg-white" data-testid="iq-coach-in-progress">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Test in uitvoering</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {answered} van de {total} vragen beantwoord. De uitslag verschijnt zodra de test is afgerond
                of de tijd is verlopen.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="neuro-card p-6 bg-white" data-testid="iq-coach-result">
          <div className="flex items-start gap-3">
            <CheckCircle size={22} className="text-[#43A047] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-display text-base font-bold tracking-tight">Uitslag</h4>
              {result?.iq_mean != null ? (
                <>
                  <div className="mt-3 inline-flex items-center gap-2 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-3 py-2 rounded-r-sm">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Geschat IQ</span>
                    <span className="font-display text-2xl font-bold tracking-tight" data-testid="iq-coach-score">
                      {Math.round(result.iq_mean)}
                    </span>
                    {result.iq_ci_low != null && result.iq_ci_high != null && (
                      <span className="text-xs text-zinc-500">
                        (95% BI: {Math.round(result.iq_ci_low)} – {Math.round(result.iq_ci_high)})
                      </span>
                    )}
                  </div>
                  {result.correct != null && result.total != null && (
                    <div className="mt-3 text-sm text-zinc-600">
                      {result.correct} van {result.total} vragen correct beantwoord.
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-zinc-600 mt-1">Geen score beschikbaar.</p>
              )}
              {session?.submitted_at && (
                <div className="mt-3 text-xs text-zinc-400">
                  Afgerond op {new Date(session.submitted_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ResetConfirm
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleReset}
          busy={resetting}
          coacheeName={coacheeName}
          testName="IQ-test"
          testid="iq-coach"
        />
      )}
    </div>
  );
}

function stateLabel(state) {
  if (state === "completed") return "Afgerond";
  if (state === "in_progress") return "Bezig";
  return "Nog niet gestart";
}

export function Empty({ coacheeName, testid, text }) {
  return (
    <div className="neuro-card p-6 bg-white" data-testid={testid}>
      <div className="flex items-start gap-3">
        <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
        <div>
          <h4 className="font-display text-base font-bold tracking-tight">Nog niet gestart</h4>
          <p className="text-sm text-zinc-600 mt-1">
            {coacheeName || "De coachee"} {text}.
          </p>
        </div>
      </div>
    </div>
  );
}

export function ResetConfirm({ onCancel, onConfirm, busy, coacheeName, testName, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onCancel()}>
      <div className="bg-white p-6 max-w-md w-full rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-reset-confirm`}>
        <h4 className="font-display text-lg font-bold tracking-tight">Test resetten?</h4>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
          De volledige uitslag en voortgang van de {testName} worden gewist.
          {" "}{coacheeName || "De coachee"} kan daarna opnieuw beginnen. Deze actie kan niet ongedaan worden gemaakt.
        </p>
        <div className="flex gap-2 mt-4 justify-end">
          <button type="button" onClick={onCancel} disabled={busy} data-testid={`${testid}-reset-cancel`} className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors">
            Annuleren
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} data-testid={`${testid}-reset-confirm-btn`} className="px-4 py-2 text-sm font-semibold uppercase tracking-wide bg-[#E53935] text-white rounded-sm hover:bg-[#C62828] transition-colors disabled:bg-zinc-400">
            {busy ? "Resetten..." : "Ja, resetten"}
          </button>
        </div>
      </div>
    </div>
  );
}
